import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { DataImportRequestType } from "../../../../utils/enums";
import { RoundedNumberFormat } from "../../../../utils/RoundedNumberFormat";
import type { EnergyDataImportRequestDetails } from "../../../EnergyData/EnergyDataUploadFlow/EnergyDataUpload";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { MissingValueCell } from "./MissingValueCell";

interface Props {
  dataImportRequestDetails?: EnergyDataImportRequestDetails[];
  dataType: DataImportRequestType;
  isLoading: boolean;
  error: unknown;
  customNoDataMessage?: string;
}

enum TableColumn {
  Meter = "meterNumber",
  Location = "locationNumber",
  Start = "startDate",
  End = "endDate",
  Metric = "measurand",
  MissingValuesShare = "missingValuesShare",
  MaxPower = "maxPower",
  SumPower = "sumPower"
}

function EnergyDataImportDetailsTable({
  dataImportRequestDetails,
  isLoading,
  dataType,
  error,
  customNoDataMessage
}: Props) {
  const columns = useMemo<
    Array<MRT_ColumnDef<EnergyDataImportRequestDetails>>
  >(() => {
    return [
      {
        header: "Bezeichner",
        accessorKey: TableColumn.Meter,
        maxSize: 140
      },
      ...(dataType === DataImportRequestType.REDISPATCH
        ? []
        : [
            {
              header: "Messlokationsnummer",
              accessorKey: TableColumn.Location,
              maxSize: 300,
              size: 200
            }
          ]),
      {
        header: "Zeitpunkt des ersten Wertes",
        accessorKey: TableColumn.Start,
        accessorFn: (row) =>
          backendDateOrDateTimeToLuxonDateTimeOrNull(row.startDate || null),
        sortFn: "datetime",
        Cell: ({ row }) => row.original.startDate
      },
      {
        header: "Zeitpunkt des letzten Wertes",
        accessorKey: TableColumn.End,
        accessorFn: (row) =>
          backendDateOrDateTimeToLuxonDateTimeOrNull(row.endDate || null),
        sortingFn: "datetime",
        Cell: ({ row }) => row.original.endDate
      },
      {
        header: "Messgröße",
        accessorKey: TableColumn.Metric
      },
      {
        header: "Anteil fehlender Werte, %",
        accessorKey: TableColumn.MissingValuesShare,
        size: 100,
        Cell: ({ row }) =>
          typeof row.original.missingValuesShare === "number" ? (
            <MissingValueCell shareValue={row.original.missingValuesShare} />
          ) : null
      },
      {
        header: "Maximale Leistung, kW",
        accessorKey: TableColumn.MaxPower,
        size: 100,
        Cell: ({ row }) => (
          <RoundedNumberFormat
            decimalScale={0}
            displayType="text"
            value={row.original.maxPower}
          />
        )
      },
      {
        header: "Summe Energie, kWh",
        accessorKey: TableColumn.SumPower,
        size: 100,
        Cell: ({ row }) => (
          <RoundedNumberFormat
            decimalScale={0}
            displayType="text"
            value={row.original.sumPower}
          />
        )
      }
    ];
  }, [dataType]);

  const mrtTable = useMantineReactTable<EnergyDataImportRequestDetails>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText:
        customNoDataMessage || "Für diesen Upload sind keine Daten vorhanden",
      initialState: {
        sorting: [
          {
            id: TableColumn.Meter,
            desc: false
          }
        ]
      },
      hideBorder: true
    }),
    enablePagination: false,
    enableToolbarInternalActions: false,
    enableBottomToolbar: false,
    columns: columns,
    data: dataImportRequestDetails || []
  });

  return (
    <LoadOrError error={error} loading={isLoading}>
      {dataImportRequestDetails && <MantineReactTable table={mrtTable} />}
    </LoadOrError>
  );
}

export { EnergyDataImportDetailsTable };
